import { format } from "date-fns";
import Papa, { ParseResult } from "papaparse";
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { useDropzone } from "react-dropzone";
import { defineMessages, FormattedMessage } from "react-intl";
import styled from "styled-components";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { FullWidthLoader } from "../../components/full-width-loader";
import { useProperty } from "../../components/property-provider";
import { SectionHeading } from "../../components/section-heading";
import { IConfig, useConfig } from "../../lib/config";
import { useApiClient, useHttpClient } from "../../lib/http";
import { regenPropertyCache } from "../common/api";
import { getPropertyConfiguration, storePropertyConfiguration } from "../shared/api/middlewareApi";
import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";

interface OperationError {
  message: string;
}

const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    createTitle: {
      id: "internalmenu.gtmManager.title.batchCreate",
      defaultMessage: "Load GTM variables into the booking engine",
    },
    editTitle: {
      id: "internalmenu.users.title.batchEdit",
      defaultMessage: "Edit users with excel file",
    },
    description: {
      id: "inventoryMultiUpdate.description",
      defaultMessage: "Update inventory",
    },
    downloadTemplate: {
      id: "userManager.exportToExcel",
      defaultMessage: "Download template",
    },
    processedSuccessfully: {
      id: "userManager.processedSuccessfully",
      defaultMessage: "Processed successfully",
    },
    dropHere: {
      id: "app.imageManager.dropHere",
      defaultMessage: "Drop files here...",
    },
  }),
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;
  > * {
    margin: 1rem 0;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1rem;
  > * {
    margin: 0 5px;
  }
  :last-child {
    margin-right: 5px;
  }
`;

const DropzoneContainer = styled.div`
  width: auto;
  height: 100px;
  border: 2px dashed #dae6e8;
  background-color: ${({ theme }) => theme.greyBackground};
  font-size: 21px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-bottom: 25px;
  outline: none;
`;

const ResultContainer = styled.div`
  margin: 16px;
`;

const buildUploadUrl = propertyTicker =>
  `/WitBookerAPI-war/webresources/endpoint/establishment/${propertyTicker}/createOrUpdateConfigurations`;

export interface GTMVariable {
  key: string;
  value: string;
}
const templateData: GTMVariable[] = [
  { key: "variable name (e.g. WIHPID)", value: "variable value (e.g. 171442)" },
];
function GTMManager() {
  const configuration: IConfig = useConfig();
  const propertyInfo = useProperty();
  const apiClient = useApiClient();
  const middlewareClient = useHttpClient(configuration.middlewareEndpoint);

  const [reFetchKey, setReFetchKey] = useState<number>(Math.random);
  const [data, setData] = useState<GTMVariable[] | undefined>(undefined);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [result, setResult] = useState<string | undefined>(undefined);
  const onDrop = useCallback(<T extends File>(acceptedFiles: T[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      return;
    }
    setFile(acceptedFiles[0]);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  useEffect(() => {
    getPropertyConfiguration(middlewareClient, propertyInfo.ticker, ["dynamicGlobalVariables"])
      .then(data => {
        if (data?.dynamicGlobalVariables && reFetchKey) {
          const parsedJSON = JSON.parse(data?.dynamicGlobalVariables);
          const variables: GTMVariable[] = Object.entries(parsedJSON).map(([key, value]) => ({
            key,
            value: value as string,
          }));
          console.log("variables", variables);
          setData(variables);
        } else {
          setData(undefined);
        }
      })
      .catch(e => {
        console.error(e);
        setError({ message: "Uh oh, error occurred fetching data" });
      });
  }, [middlewareClient, propertyInfo.ticker, reFetchKey]);

  const handleImport = useCallback(async () => {
    if (!file) {
      return;
    }
    setLoading(true);
    setError(undefined);
    try {
      const csvString = await file.text();
      const results: ParseResult<{ key: string; value: string }> = Papa.parse(csvString, {
        header: true,
      });
      if (results.errors?.length) {
        setError({ message: "CSV Has errors" });
        setLoading(false);
        return;
      }
      const validRows = results.data.filter(it => Boolean(it.key) && Boolean(it.value));
      const fromEntries = Object.fromEntries(validRows.map(({ key, value }) => [key, value]));

      const response: string = await storePropertyConfiguration(
        middlewareClient,
        propertyInfo.ticker,
        {
          dynamicGlobalVariables: JSON.stringify(fromEntries),
        }
      );

      if (response.length && response !== "ok") {
        setError({ message: "Failed to load CSV file" });
      } else {
        await regenPropertyCache(apiClient, propertyInfo.ticker);
        setReFetchKey(Math.random());
      }
      setResult(response);
    } catch (err) {
      setError({ message: "Access denied" });
    }
    setLoading(false);
  }, [apiClient, file, middlewareClient, propertyInfo.ticker]);

  if (isLoading) {
    return <FullWidthLoader />;
  }

  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.createTitle} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        {data?.length ? (
          <Container>
            <div>Current data:</div>
            <table>
              <thead>
                <tr>
                  <th style={{ border: "1px solid black", padding: "8px" }}> key</th>
                  <th style={{ border: "1px solid black", padding: "8px" }}> value</th>
                </tr>
              </thead>
              <tbody>
                {data.map(({ key, value }) => (
                  <tr>
                    <td style={{ border: "1px solid black", padding: "8px" }}>{key}</td>
                    <td style={{ border: "1px solid black", padding: "8px" }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Container>
        ) : null}
        <Container>
          <div>
            <FormattedMessage {...messages.description} />
          </div>
          <DropzoneContainer {...getRootProps()}>
            <input {...getInputProps()} />
            {file ? <div>{file.name}</div> : <FormattedMessage {...messages.dropHere} />}
          </DropzoneContainer>
        </Container>
        {error && (
          <Alert variant="danger">
            <strong>
              <FormattedMessage {...messages.saveError} />
            </strong>
          </Alert>
        )}
        <ButtonContainer>
          <Button variant="primary" type="button" onClick={handleImport}>
            <FormattedMessage {...messages.import} />
          </Button>

          <CSVLink
            style={{ marginLeft: "10px", marginRight: "18px" }}
            data={data ?? templateData}
            filename={`gtm_manager_${propertyInfo?.ticker}_${format(new Date(), "yyyyMMdd")}.csv`}>
            <Button variant="primary">
              {data ? (
                <FormattedMessage {...messages.export} />
              ) : (
                <FormattedMessage {...messages.downloadTemplate} />
              )}
            </Button>
          </CSVLink>
        </ButtonContainer>
        {result && (
          <ResultContainer>
            <Alert variant="success">
              <strong>
                <FormattedMessage {...messages.processedSuccessfully} />
              </strong>
            </Alert>
          </ResultContainer>
        )}
      </Card>
    </>
  );
}

export default withAuthorizationVerifier(GTMManager);
